import React, { useEffect, useMemo, useState } from "react";
import '../css/animate.css';
import '../css/barvy.css';
import '../css/style.css';
import { AnimatePresence, motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { getLocalizedText } from '../utils/textLocalization';
import {
  EditableContent,
  useEditry,
  usePreloadPublicData,
  usePublicContentList,
  getModelEntryData,
} from '@lazylabs-dev/editry-react';
import { isEditryConfigured } from '../utils/editryConfig';
import { getCurrentLanguage } from '../utils/languageSwitcher';

const LOCALE_TO_CONTENT_LANGUAGE = {
  'cs-CZ': 'cs',
  'sk-SK': 'sk',
  'pl-PL': 'pl',
  'hu-HU': 'hu',
};

const getPreferredLanguages = (locale) => {
  const preferred = LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';
  return preferred === 'cs' ? ['cs', 'default'] : [preferred, 'cs', 'default'];
};

const getEditryLanguage = (locale) => LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';

const normalizeContentLanguage = (language) => {
  if (typeof language !== 'string') {
    return 'default';
  }

  const normalized = language.trim().toLowerCase();
  return normalized || 'default';
};

const findLocalizedRecord = (contentList, contentKey, preferredLanguages = ['cs']) => {
  if (!Array.isArray(contentList)) {
    return null;
  }

  const records = contentList.filter((item) => item.content_key === contentKey);
  if (!records.length) {
    return null;
  }

  for (const language of preferredLanguages) {
    const targetLanguage = normalizeContentLanguage(language);
    const record = records.find((item) => normalizeContentLanguage(item.language) === targetLanguage);
    if (record) {
      return record;
    }
  }

  return records[0] || null;
};

const getLocalizedTextValue = (contentList, contentKey, fallback = '', preferredLanguages = ['cs']) => {
  const record = findLocalizedRecord(contentList, contentKey, preferredLanguages);
  const value = record?.value;

  if (typeof value === 'string' && value.trim() !== '') {
    return value;
  }

  return fallback;
};

const getLocalizedListValue = (contentList, contentKey, preferredLanguages = ['cs']) => {
  const record = findLocalizedRecord(contentList, contentKey, preferredLanguages);
  const value = record?.value;

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const getBikeRedirect = (element = {}) => (
  element.redirect || element.odkaz || element.link || element.url || '#'
);

const getBikeImage = (element = {}) => (
  element.obrazek || element.image || ''
);

const getBikeName = (element = {}) => (
  element.nazev || element.name || ''
);

const getBikeDescription = (element = {}) => (
  element.popis || element.description || ''
);

const getBikeCategory = (element = {}) => (
  element.category || ''
)

function App() {
  const locale = getCurrentLanguage();
  const preferredLanguages = useMemo(() => getPreferredLanguages(locale), [locale]);
  const editryLanguage = useMemo(() => getEditryLanguage(locale), [locale]);
  const editryReady = isEditryConfigured();
  const { overlayEnabled, adminSession } = useEditry();
  const isEditModeActive = overlayEnabled && Boolean(adminSession?.token);

  usePreloadPublicData({ auto: editryReady });
  const contentListResponse = usePublicContentList();

  const allContent = useMemo(
    () => contentListResponse.data?.data || [],
    [contentListResponse.data]
  );

  const [bikes, setBikes] = useState([]);
  const [konfgalerie, setGalerie] = useState([]);
  const { client } = useEditry();

  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      if (!editryReady) {
        setBikes([]);
        setGalerie([]);
        return;
      }

      try {
        const modelsResponse = await client.getPublicModels();
        const konfigurator = modelsResponse?.data?.find((model) => model.slug === 'konfigurator');
        const galerie = modelsResponse?.data?.find((model) => model.slug === 'konfigurator-galerie');

        if (!konfigurator || !galerie) {
          if (!cancelled) {
            setBikes([]);
            setGalerie([]);
          }
          return;
        }

        let itemsResponse = await client.getPublicModelItems(konfigurator.id, editryLanguage);
        let items = itemsResponse?.data || [];

        let itemsResponseGalerie = await client.getPublicModelItems(galerie.id, editryLanguage);
        let galerieItems = itemsResponseGalerie?.data || [];

        if (!items.length) {
          itemsResponse = await client.getPublicModelItems(konfigurator.id);
          items = itemsResponse?.data || [];
        }

        if (!galerieItems.length) {
          itemsResponseGalerie = await client.getPublicModelItems(galerie.id);
          galerieItems = itemsResponseGalerie?.data || [];
        }

        const parsed = items
          .map((item) => {
            const fields = getModelEntryData(item);
            return {
              obrazek: fields.obrazek || item.slug || '',
              nazev: fields.nazev || item.slug || '',
              popis: fields.popis || item.slug || '',
              redirect: fields.redirect || item.slug || '',
              category: fields.category || fields.kategorie || '', 
            };
          })
          .filter((item) => item.obrazek && item.nazev && item.popis && item.redirect);

        const parsedGalerie = galerieItems
          .map((item) => {
            const fields = getModelEntryData(item);
            return {
              obrazek: fields.obrazek || item.slug || '',
            };
          })
          .filter((item) => item.obrazek);

        if (!cancelled) {
          setBikes(parsed);
          setGalerie(parsedGalerie);
        }
      } catch (error) {
        if (!cancelled) {
          setBikes([]);
          setGalerie([]);
        }
      }
    };

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, [client, editryLanguage, editryReady]);

  const data = useMemo(() => ({
    nazev: getLocalizedTextValue(allContent, 'konfigurator_konfigurator_nazev', '', preferredLanguages),
    popis: getLocalizedTextValue(allContent, 'konfigurator_konfigurator_popis', '', preferredLanguages),
    kola: bikes,
  }), [allContent, preferredLanguages, bikes]);

  const categories = useMemo(() => {
    const allCats = data.kola.map(bike => getBikeCategory(bike)).filter(Boolean);
    return [...Array.from(new Set(allCats))];
  }, [data.kola]);

  const galerie = useMemo(
    () => getLocalizedListValue(allContent, 'konfigurator_konfigurator_galerie', preferredLanguages),
    [allContent, preferredLanguages]
  );

  const kolaFallbackJson = useMemo(() => JSON.stringify(data.kola || [], null, 2), [data.kola]);
  const galerieFallbackJson = useMemo(() => JSON.stringify(galerie || [], null, 2), [galerie]);

  const [activeGallery, setActiveGallery] = useState(1);
  const [, setGallerTime] = useState(10);

  const configureText = getLocalizedText('configurator.configure');
  const openText = getLocalizedText('configurator.open');
  const customerConfigsText = getLocalizedText('configurator.customer_configs');

  useEffect(() => {
    document.title = getLocalizedText('configurator.page_title');
  }, []);

  useEffect(() => {
    if (!galerie.length) {
      return undefined;
    }

    const interval = setInterval(() => {
      setGallerTime(prev => {
        if (prev === 1) {
          setActiveGallery(prevGallery => {
            if (prevGallery === galerie.length) {
              return 1;
            } else {
              return prevGallery + 1;
            }
          });
          return 10;
        } else {
          return prev - 1;
        }
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [galerie.length]);

  const renderEditableText = (contentKey, fallback = '') => {
    if (!isEditModeActive) {
      return fallback;
    }

    return (
      <EditableContent
        contentKey={contentKey}
        language={editryLanguage}
        as="span"
        fallback={fallback}
        showFallbackWhileLoading={false}
      />
    );
  };

  const gradienty = [
    "linear-gradient(160deg, rgba(255, 62, 62, 0.4) 0%, rgba(143, 174, 118, 0) 65%, rgba(237, 221, 83, 0) 100%)",
    "linear-gradient(160deg, rgba(0, 255, 255, 0.4) 0%, rgba(0, 255, 128, 0) 65%, rgba(255, 255, 0, 0) 100%)",
    "linear-gradient(160deg, rgba(255, 231, 96, 0.4) 0%, rgba(255, 255, 255, 0) 65%, rgba(0, 0, 0, 0) 100%)",
    "linear-gradient(160deg, rgba(78, 255, 62, 0.4) 0%, rgba(143, 174, 118, 0) 65%, rgba(237, 221, 83, 0) 100%)",
    "linear-gradient(160deg, rgba(0, 102, 255, 0.4) 0%, rgba(0, 255, 128, 0) 65%, rgba(255, 255, 0, 0) 100%)",
    "linear-gradient(160deg, rgba(183, 96, 255, 0.4) 0%, rgba(255, 255, 255, 0) 65%, rgba(0, 0, 0, 0) 100%)"
  ];

  return (
    <AnimatePresence mode="wait">
      <motion.div className="configurator-background" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        <div className="configurator-background-gradient"/>
        <motion.div className="page configurator" key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
          <h5>{renderEditableText('konfigurator_konfigurator_nazev', data.nazev)}</h5>
          <p className="configurator-text">{renderEditableText('konfigurator_konfigurator_popis', data.popis)}</p>
          <NavLink to="https://www.nukeproof.com/en_CZ/configurator"><button className="configurator-undertext-button"><span>{configureText}</span></button></NavLink>
          
          <motion.div className="configurator-phases" key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
            <h2 className="configurator-nukeproof">{customerConfigsText}</h2>
            
            <div className="tags-rework">
              {categories.map((cat) => (
                <button onClick={() => setSelectedCategory(selectedCategory === cat ? "all" : cat)} className={selectedCategory !== cat ? "tags-rework-active" : ""}>
                  <span>{cat}</span>
                </button>
              ))}
            </div>

            {isEditModeActive && (
              <div className="contact-region" style={{ width: '100%', marginBottom: 20 }}>
                <h3 className="info-title">Kola (JSON):</h3>
                <EditableContent
                  contentKey="konfigurator_konfigurator_kola"
                  language={editryLanguage}
                  as="pre"
                  className="info-content"
                  contentType="list"
                  fallback={kolaFallbackJson}
                  showFallbackWhileLoading={false}
                />
              </div>
            )}

            <div className="conf-list-bikes-rework">
              {categories
                .filter((cat) => cat !== "all")
                .filter((cat) => selectedCategory === "all" || selectedCategory === cat)
                .map((categoryName) => {
                  const bikesInThisCategory = data.kola.filter(
                    (bike) => getBikeCategory(bike) === categoryName
                  );

                  if (bikesInThisCategory.length === 0) return null;
              

                  return (
                    <>
                      <h3 className="configurator-nukeproof-category-text">{categoryName || "Bez kategorie"}</h3>
                  
                      <>
                        {bikesInThisCategory.map((element, index) => {
                        
                          return (
                          <NavLink key={index} to={getBikeRedirect(element)}>
                            <div>
                              <img 
                                src={`${getBikeImage(element)}?format=webp`} 
                                style={{ background: gradienty[Math.floor(Math.random() * gradienty.length)] }} 
                                alt={getBikeName(element) || 'Konfigurovatelné kolo'}
                              />
                              <h3>{getBikeName(element)}</h3>
                              <p>{getBikeDescription(element)}</p>
                              <h4><span>{openText}</span></h4>
                            </div>
                          </NavLink>
                        )})}
                      </>
                    </>
                  );
                })}
            </div>
          </motion.div>

          <motion.div className="configurator-galerie" key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              {isEditModeActive && (
                <div className="contact-region" style={{ width: '100%', marginBottom: 20 }}>
                  <h3 className="info-title">Galerie (JSON):</h3>
                  <EditableContent
                    contentKey="konfigurator_konfigurator_galerie"
                    language={editryLanguage}
                    as="pre"
                    className="info-content"
                    contentType="list"
                    fallback={galerieFallbackJson}
                    showFallbackWhileLoading={false}
                  />
                </div>
              )}
              <div className="conf-gallery">
              {konfgalerie && (konfgalerie.map((element, key) => (
                <div key={key} className={activeGallery === key+1 ? "conf-gallery-active" : ""} onClick={() => {setActiveGallery(key+1); setGallerTime(10)}}>
                  <img src={`${getBikeImage(element)}?format=webp`} alt="Konfigurator galerie"/>
                </div>
              )))}
              </div>
              <div className="conf-gallery-arrows">
                {konfgalerie && konfgalerie.map((element, key) => (
                  <button key={key} onClick={() => {setActiveGallery(key+1); setGallerTime(10)}} className={activeGallery === key+1 ? "conf-gallery-arrows-active" : ""} />
                ))}
              </div>
          </motion.div>

        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default App;